import './polyfills'
import React, { useEffect } from 'react'

import ReactDOM from 'react-dom'
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  concat,
  createHttpLink,
  Observable,
  split
} from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import 'firebase/messaging'
import ConfigurableValues from './config/constants'
import { ConfigurationProvider } from './context/Configuration'
import App from './app'
import { RestProvider } from './context/Restaurant'
import { ThemeProvider, StyledEngineProvider } from '@mui/material'
import theme from './utils/theme'
import { endRejectedSession } from './utils/authToken'

function Main() {
  const { SERVER_URL, WS_SERVER_URL } = ConfigurableValues()

  const cache = new InMemoryCache({
    typePolicies: {
      OrderItem: { keyFields: false },
      OrderAddon: { keyFields: false },
      OrderOption: { keyFields: false }
    }
  })
  const httpLink = createHttpLink({
    uri: `${SERVER_URL}/graphql`
  })

  const wsClient = createClient({
    url: `${WS_SERVER_URL}/graphql`,
    connectionParams: () => {
      let token = null
      const vendorData = localStorage.getItem('user-enatega')
      if (vendorData) token = JSON.parse(vendorData).token
      if (!token) {
        const staffData = localStorage.getItem('staff-session')
        if (staffData) token = JSON.parse(staffData).token
      }
      return {
        authorization: token ? `Bearer ${token}` : ''
      }
    }
  })

  const wsLink = new GraphQLWsLink(wsClient)
  const request = async operation => {
    let token = null
    const vendorData = localStorage.getItem('user-enatega')
    if (vendorData) {
      token = JSON.parse(vendorData).token
    }
    // Fall back to staff-session token (SERVER role doesn't set user-enatega)
    if (!token) {
      const staffData = localStorage.getItem('staff-session')
      if (staffData) token = JSON.parse(staffData).token
    }
    operation.setContext({
      headers: {
        authorization: token ? `Bearer ${token}` : ''
      }
    })
  }

  const requestLink = new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        let handle
        Promise.resolve(operation)
          .then(oper => request(oper))
          .then(() => {
            handle = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer)
            })
          })
          .catch(observer.error.bind(observer))

        return () => {
          if (handle) handle.unsubscribe()
        }
      })
  )
  // The server marks a response whose login it refused (backend
  // middleware/auth.js): sign out and show the login page. Runs before the
  // result reaches the page, so a login that answers with a fresh token
  // stores it after the dead one is cleared.
  const sessionLink = new ApolloLink((operation, forward) =>
    forward(operation).map(result => {
      if (result.extensions && result.extensions.session === 'rejected') {
        const sent = (operation.getContext().headers || {}).authorization || ''
        endRejectedSession(sent.replace('Bearer ', ''), client)
      }
      return result
    })
  )
  const terminatingLink = split(({ query }) => {
    const { kind, operation } = getMainDefinition(query)
    return kind === 'OperationDefinition' && operation === 'subscription'
  }, wsLink)

  const client = new ApolloClient({
    link: concat(ApolloLink.from([terminatingLink, requestLink, sessionLink]), httpLink),
    cache,
    resolvers: {},
    connectToDevTools: true
  })

  return (
    <ApolloProvider client={client}>
      <ConfigurationProvider>
        {/* <LoadScript
          id="script-loader"
          googleMapsApiKey={GOOGLE_MAPS_KEY}
          libraries={[
            'drawing',
            'places',
            'geometry',
            'localContext',
            'visualization'
          ]}> */}

        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <RestProvider>
              {/* <GoogleMapsLoader> */}
              <App />
              {/* </GoogleMapsLoader> */}
            </RestProvider>
          </ThemeProvider>
        </StyledEngineProvider>

        {/* </LoadScript> */}
      </ConfigurationProvider>
    </ApolloProvider>
  )
}

// eslint-disable-next-line react/no-deprecated
ReactDOM.render(<Main />, document.getElementById('root'))
